import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_MODE = process.env.REACT_APP_STATIC_MODE === "true";
const STATIC_EMAIL = "juan@sahagunlandscape.com";

const INITIAL = {
  name: "",
  phone: "",
  property_type: "Residential Property",
  service: "Landscape Design & Hardscape",
  details: "",
};

const inputCls =
  "px-4 py-3 bg-[#faf8f5] border border-[#2d4a22]/30 text-sm focus:border-[#2d4a22] focus:outline-none w-full";
const labelCls = "text-xs uppercase font-bold tracking-wider text-[#1f281e]";

export const QuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (STATIC_MODE) {
        // Static hosting: deliver the quote straight to Juan's inbox via FormSubmit
        const res = await fetch(`https://formsubmit.co/ajax/${STATIC_EMAIL}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            _subject: `New Quote Request - ${form.service}`,
            _template: "table",
            name: form.name,
            phone: form.phone,
            property_type: form.property_type,
            service: form.service,
            details: form.details || "-",
          }),
        });
        if (!res.ok) throw new Error("send failed");
      } else {
        await axios.post(`${API}/quotes`, form);
      }
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Quote request sent. Juan will reach out shortly.");
    } catch (err) {
      toast.error("Could not send your request. Please call +1 (323) 220-4640.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#f2ece1] text-[#1f281e]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center max-w-xl mx-auto mb-12 reveal">
          <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
            Start Your Project
          </span>
          <h2 className="font-display text-3xl md:text-4xl uppercase text-[#1f281e]">Request A Free Quote</h2>
          <p className="text-sm text-[#3d4b3b] mt-2">
            Speak directly with Juan Sahagun for quick scheduling and fair pricing.
          </p>
        </div>

        <div className="reveal reveal-d1 bg-[#faf8f5] p-8 md:p-10 border border-[#2d4a22]/20 shadow-sm">
          {submitted ? (
            <div className="text-center py-10" data-testid="quote-success">
              <svg className="w-12 h-12 mx-auto mb-4 text-[#2d4a22]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <h3 className="font-display text-2xl uppercase mb-2">Request Received</h3>
              <p className="text-sm text-[#3d4b3b] mb-6">
                Thank you. Juan Sahagun will contact you to schedule your free quote.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="quote-another-btn"
                className="px-8 py-3 border border-[#2d4a22] text-[#2d4a22] text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#2d4a22]/5 transition-colors"
              >
                Send Another Request
              </button>
            </div>
          ) : (
            <form className="grid grid-cols-1 sm:grid-cols-2 gap-6" onSubmit={handleSubmit} data-testid="quote-form">
              <div className="flex flex-col gap-1">
                <label htmlFor="quote-name" className={labelCls}>Your Name</label>
                <input
                  id="quote-name"
                  data-testid="quote-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls}
                  placeholder="First and last name"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="quote-phone" className={labelCls}>Phone Number</label>
                <input
                  id="quote-phone"
                  data-testid="quote-phone-input"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                  placeholder="(323) 000-0000"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="quote-property" className={labelCls}>Property Type</label>
                <select
                  id="quote-property"
                  data-testid="quote-property-select"
                  value={form.property_type}
                  onChange={update("property_type")}
                  className={inputCls}
                >
                  <option>Residential Property</option>
                  <option>Commercial Grounds</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="quote-service" className={labelCls}>Service Needed</label>
                <select
                  id="quote-service"
                  data-testid="quote-service-select"
                  value={form.service}
                  onChange={update("service")}
                  className={inputCls}
                >
                  <option>Landscape Design &amp; Hardscape</option>
                  <option>Irrigation System Work</option>
                  <option>Turf or Sod Installation</option>
                  <option>Tree Services</option>
                  <option>Gardening Services</option>
                  <option>Full Service Maintenance</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex flex-col gap-1">
                <label htmlFor="quote-details" className={labelCls}>Project Details</label>
                <textarea
                  id="quote-details"
                  data-testid="quote-details-textarea"
                  rows="3"
                  value={form.details}
                  onChange={update("details")}
                  className={inputCls}
                  placeholder="Describe your yard or grounds..."
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-btn"
                  className="w-full py-4 bg-[#2d4a22] text-[#faf8f5] text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#1a2d14] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Submit Quote Request"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
